/**
 * PRIVATE MESSAGE — the composer the player card's Send Message opens (owner's assignment 7.10.2026: during a shared running
 * match to anyone at the table, after the match only to friends). Codex's stage-3 layout (art/src/player-card-2026-10-07/
 * message/layout-message.json, VALUES-message.md): the HOW TO PLAY frame with its live title and rule, the MESSAGE CREW field,
 * SEND and note — nothing new (owner: "me ei tehdä uusia buttoneita"). The recipient is frozen when the popup opens; a phase
 * or target change never retargets the draft or touches the game's clock.
 *
 * The one optional lane shows the recipient's newest message to me (one line, ellipsis) so a reply needs no thread view; the
 * lane stays reserved when empty so the field and SEND never move. Sending closes the popup and the game's toast says Sent;
 * FRIENDS_ONLY (the match ended, not friends) keeps the draft and darkens SEND with the note "Friends only after the match".
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { art } from '../art';
import { messageTo, showToast } from '../state/store';
import { messages } from '../net/api';
import { PopupPanel } from './PopupPanel';
import { sfx } from '../audio/sfx';
import { popup as POPUP, behavior as BEHAVIOR } from '../layouts/player-card-2026-10-07/message/layout-message.json';

const PLACE = (POPUP as unknown as { place: { x: number; y: number; w: number } }).place;
const MAX = (BEHAVIOR as unknown as { maxLength: number }).maxLength;

export function MessageSheet() {
  const to = messageTo.value;
  // the recipient is a snapshot: whatever the game does meanwhile, this draft goes where it was opened for
  const [rcpt] = useState(to);
  const [text, setText] = useState(''); const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(true); const [last, setLast] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!rcpt) return;
    let live = true;
    void messages.thread(rcpt.id).then((t) => {
      if (!live) return;
      setAllowed(t.canMessage);
      const mine = [...t.messages].reverse().find((m) => m.fromId === rcpt.id);   // the newest INCOMING line, not simply the newest
      setLast(mine ? mine.text.replace(/[\r\n]+/g, ' ') : null);
    }).catch(() => { /* the lane just stays empty; the send itself tells if it is not allowed */ });
    return () => { live = false; };
  }, [rcpt?.id]);
  if (!to || !rcpt) return null;
  const close = () => { messageTo.value = null; };
  const draft = text.trim();
  const off = busy || !allowed || !draft || draft.length > MAX;
  const send = async () => {
    if (off) return; setBusy(true); sfx.tap();
    try { await messages.send(rcpt.id, draft); close(); showToast('Sent'); }
    catch (e) {
      setBusy(false);
      const code = (e as { code?: string }).code;
      if (code === 'FRIENDS_ONLY') { setAllowed(false); return; }   // the draft stays; SEND darkens; the note says why
      showToast(code === 'TOO_LONG' ? 'Too long' : 'Could not send');
    }
  };
  const plates = { '--chip': `url(${art('/art/lobby-kit/pill.large.png')})`, '--cta': `url(${art('/art/lobby-kit/cta.plate.png')})` } as Record<string, string>;
  return (
    <div class="sheet-backdrop howto-backdrop" onClick={close} data-testid="message-sheet" data-state={!allowed ? 'not_allowed' : busy ? 'sending' : draft ? 'armed' : 'idle'}>
      <PopupPanel title={`MESSAGE ${rcpt.name.toUpperCase()}`} label={`Message ${rcpt.name}`} onClose={close} closeTestid="message-close" place={PLACE} cls="pm-panel">
        <p class={`pm-last${last ? '' : ' none'}`} data-testid="message-last" aria-label={last ? 'Full last incoming message' : undefined} title={last ?? undefined}>{last ? `${rcpt.name}: ${last}` : ''}</p>
        <div class="mc-field" style={plates} onClick={() => input.current?.focus()}>
          <img class="mc-mail" src={art('/art/lobby-kit/icon.paper-plane.png')} alt="" draggable={false} />
          <input ref={input} type="text" value={text} maxLength={MAX} placeholder={`Write to ${rcpt.name}…`} aria-label={`Write to ${rcpt.name}`} data-testid="message-input" autocomplete="off" enterkeyhint="send"
            onInput={(e) => setText((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === 'Enter' && !off) void send(); }} />
        </div>
        <button class={`mc-send${off ? ' off' : ''}`} type="button" style={plates} data-testid="message-send" aria-label="Send" aria-disabled={off || undefined}
          onClick={(ev) => { ev.stopPropagation(); void send(); }}>SEND</button>
        <p class="mc-note" data-testid="message-note">{busy ? 'Sending…' : allowed ? `Only ${rcpt.name} sees it` : 'Friends only after the match'}</p>
      </PopupPanel>
    </div>
  );
}
